import { getBetweenDay, getDateFromMoment } from "core/constants/globalfunc";

export const normalizeSelectValue = (v: any): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    const nested = (v as any)?.value;
    if (nested === null || nested === undefined) return "";
    if (typeof nested === "string") return nested;
    if (typeof nested === "number") return String(nested);
  }
  return "";
};

export const buildAddressPayload = (
  form: any,
  partnerCountry: string,
  overrides?: Partial<{
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  }>
) => {
  return {
    city: overrides?.city || form.customerCity,
    country: overrides?.country || partnerCountry,
    line1: overrides?.line1 || form.customerAddressLine1,
    line2: overrides?.line2 || form.customerAddressLine2 || "",
    primary: true,
    state: overrides?.state || normalizeSelectValue(form.customerState) || "",
    zip: overrides?.zip || form.customerZIP || "",
  };
};

export const buildCustomerPayload = (
  form: any,
  partnerCountry: string,
  addressOverrides?: Partial<{
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  }>
) => {
  return {
    birthday: `${form?.customerBirthdayYr || ""}-${form?.customerBirthdayMo || ""}-${form?.customerBirthdayDa || ""}`,
    firstName: form?.customerNameFirst || "",
    lastName: form?.customerNameLast || "",
    company: form?.companyName || "",
    middleName: "",
    towingExperience:
      form?.towingExperience?.value === "-1"
        ? ""
        : form?.towingExperience?.value,
    address: buildAddressPayload(form, partnerCountry, addressOverrides),
    email: {
      email: form.customerEmail,
      primary: true,
      consentMarketing: false,
      consentNotification: false,
    },
    phone: {
      phone: form.phone,
      country: partnerCountry,
      prefix: form.prefix,
      extension: form.extension,
      primary: true,
      consentMarketing: false,
      consentNotification: false,
      consentSmsMarketing: false,
      consentSmsNotification: false,
    },
    customerId: "",
  };
};

export const getEffectivePaymentProvider = (
  provider: string,
  paymentError: boolean
): string => (paymentError ? "Cash" : provider);

export const buildBookingPayload = (
  checkoutItem: any,
  filterDate: any,
  discountCode: any,
  selectedServiceIds: number[],
  paymentProvider: string,
  customerId: any
) => {
  const assetId = checkoutItem?.assetId;
  if (assetId == null || assetId === '') {
    throw new Error('Checkout item is missing assetId');
  }

  return {
    assetId,
    customerId,
    pickupDate: getDateFromMoment(filterDate?.pickupDate),
    pickupTime: filterDate?.pickupTime,
    rentalDays: getBetweenDay(
      new Date(filterDate?.pickupDate),
      new Date(filterDate?.returnDate)
    ),
    returnDate: getDateFromMoment(filterDate?.returnDate),
    returnTime: filterDate?.returnTime,
    surcharge: 0,
    promoCode: discountCode,
    serviceIds: selectedServiceIds,
    origin: paymentProvider,
  };
};

export function getSelectedServiceIdsForAsset(
  selectedAddonServiceIdsByAssetId: Record<string, number[] | undefined> | undefined,
  assetId: string | number | undefined,
): number[] {
  if (assetId == null || assetId === '') return [];
  return selectedAddonServiceIdsByAssetId?.[String(assetId)] || [];
}

export const normalizeCountryCode = (value: any) => {
  const raw =
    typeof value === "string"
      ? value.trim()
      : value && typeof value === "object"
        ? `${value.value ?? value.label ?? ""}`.trim()
        : "";

  const upper = raw.toUpperCase();
  const countryMap: Record<string, string> = {
    US: "US",
    USA: "US",
    "UNITED STATES": "US",
    "UNITED STATES OF AMERICA": "US",
    CA: "CA",
    CAN: "CA",
    CANADA: "CA",
  };

  return countryMap[upper] || (upper.length === 2 ? upper : "US");
};

export const normalizeFinixCountryCode = (value: any) => {
  const countryCode = normalizeCountryCode(value);
  const finixCountryMap: Record<string, string> = {
    US: "USA",
    CA: "CAN",
  };

  return finixCountryMap[countryCode] || "USA";
};

export type BillingFormValues = {
  customerId: string | number;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  email?: string;
  phone?: string;
  birthday?: string;
  partnerCountry: string;
};

export function getApiErrorMessage(err: unknown, defaultMsg: string): string {
  if (typeof err === "string" && err.trim()) return err;

  if (err instanceof Error && err.message && !err.message.startsWith("Request failed with status")) {
    return err.message;
  }

  const data = (err as { response?: { data?: unknown }; message?: string })?.response?.data ?? err;

  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const record = data as {
      message?: string;
      error?: string | { message?: string };
    };

    if (record.message) return record.message;

    if (typeof record.error === "string" && record.error.trim()) return record.error;

    if (record.error && typeof record.error === "object" && record.error.message) {
      return record.error.message;
    }
  }

  if (err instanceof Error && err.message) return err.message;

  return defaultMsg;
}

export const buildCustomerPayloadFromBilling = ({
  customerId,
  firstName,
  lastName,
  address,
  city,
  state,
  zip,
  email = "",
  phone = "",
  birthday = "",
  partnerCountry,
}: BillingFormValues) => {
  return {
    customerId: String(customerId),
    birthday,
    firstName,
    lastName,
    middleName: "",
    towingExperience: "",
    address: {
      city,
      country: partnerCountry,
      line1: address,
      line2: "",
      primary: true,
      state,
      zip,
    },
    email: {
      email,
      primary: true,
      consentMarketing: false,
      consentNotification: false,
    },
    phone: {
      phone,
      country: partnerCountry,
      prefix: "+1",
      extension: "",
      primary: true,
      consentMarketing: false,
      consentNotification: false,
      consentSmsMarketing: false,
      consentSmsNotification: false,
    },
  };
};
